import {objectFromEntries} from '@benchling/js-utils';

import {AnalysisGraphQL} from 'analysis-tool/AnalysisTypes';
import {findAndAssertSingleStepDataFrame} from 'analysis-tool/data/findAndAssertStepDataFrameIds';
import {getLegacyNullableStepFromAnalysis} from 'analysis-tool/data/getStepFromAnalysis';
import {assertAndContinue} from 'Asserts';
import {DataFrameColumnDef} from 'data-frames/DataFrameTypes';
import {ColumnTypeValidationFlag, isValidColumnType} from 'data-frames/util/validateColumnType';
import {LeafType} from 'data-types/benchling-type-def/BenchlingTypeDef';
import {parseRows} from 'external-files/ExternalFileUtils';
import {PipelineStepStatusCode, PipelineStepType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {getChildSteps} from 'pipelines/StepLineageUtils';

export enum AutoMapperMode {
  NAME = 'NAME',
  ORDER = 'ORDER',
}

export enum AutoMapperColumnJoinType {
  KEEP_TEMPLATE = 'KEEP_TEMPLATE',
  KEEP_SHARED = 'KEEP_SHARED',
  KEEP_ALL = 'KEEP_ALL',
}

export interface AutoMapperOption {
  mode: AutoMapperMode;
  columnJoinType: AutoMapperColumnJoinType;
}

const MAX_EDIT_DISTANCE_ALLOWED = 0.2;

/**
 * Check if the selected column type is compatible with the template column.
 */
export function isColumnTypeCompatible(
  selectedColumn: Pick<DataFrameColumnDef, 'type'>,
  templateColumn: Pick<DataFrameColumnDef, 'type'>,
  options?: {alwaysAllowNullable: boolean}
): boolean {
  return isValidColumnType(selectedColumn.type, {
    allowedLeafTypes: [templateColumn.type.leafType],
    nullableType:
      templateColumn.type.isNullable || options?.alwaysAllowNullable
        ? ColumnTypeValidationFlag.ALLOWED
        : ColumnTypeValidationFlag.NOT_ALLOWED,
    listType: templateColumn.type.isList
      ? ColumnTypeValidationFlag.REQUIRED
      : ColumnTypeValidationFlag.NOT_ALLOWED,
    maybeTextType: templateColumn.type.maybeText
      ? ColumnTypeValidationFlag.ALLOWED
      : ColumnTypeValidationFlag.NOT_ALLOWED,
  });
}

/**
 * Get the edit distance between two strings
 *
 * Edit distance represents the minimum number of single-character edits (insertions, deletions, or
 * substitutions) required to change one string into the other.
 *
 * This function uses dynamic programming to compute the distance. It builds a matrix where the element
 * at (i, j) represents the edit distance between the first i characters of the first string and the first
 * j characters of the second string.
 *
 * See this Wikipedia article for more information
 * https://en.wikipedia.org/wiki/Levenshtein_distance
 */
export function getEditDistance(s1: string, s2: string): number {
  const dpMatrix = Array.from({length: s1.length + 1}, () => Array(s2.length + 1).fill(0));

  dpMatrix.forEach((row, rowIndex) => {
    row[0] = rowIndex;
  });
  dpMatrix[0].forEach((_, colIndex) => {
    dpMatrix[0][colIndex] = colIndex;
  });

  for (let rowIndex = 1; rowIndex <= s1.length; rowIndex++) {
    for (let colIndex = 1; colIndex <= s2.length; colIndex++) {
      if (s1[rowIndex - 1] === s2[colIndex - 1]) {
        dpMatrix[rowIndex][colIndex] = dpMatrix[rowIndex - 1][colIndex - 1];
      } else {
        // Calculate costs of deletion, insertion, and substitution
        const deleteCost = dpMatrix[rowIndex - 1][colIndex] + 1;
        const insertCost = dpMatrix[rowIndex][colIndex - 1] + 1;
        const substituteCost = dpMatrix[rowIndex - 1][colIndex - 1] + 1;

        dpMatrix[rowIndex][colIndex] = Math.min(deleteCost, insertCost, substituteCost);
      }
    }
  }

  return dpMatrix[s1.length][s2.length];
}

/**
 * Get relative edit distance between two columns.
 *
 * The relative edit distance is the edit distance of the display names of the two columns divided by the
 * maximum length of the display names. If the column types are not compatible, the relative edit distance is
 * increased by 1 and the maximum length is also increased by 1.
 */
function getRelativeEditDistanceBetweenColumns(
  templateColumn: DataFrameColumnDef,
  selectedColumn: DataFrameColumnDef
): number {
  const distance = getEditDistance(templateColumn.displayName, selectedColumn.displayName);
  const maxLength = Math.max(templateColumn.displayName.length, selectedColumn.displayName.length);
  return (distance + (isColumnTypeCompatible(selectedColumn, templateColumn) ? 0 : 1)) / (maxLength + 1);
}

/**
 * Convert a mapping where values are full column defs to a mapping where values are column system names,
 * excluding any null values.
 */
function convertColumnDefMappingToSystemNameMapping(
  mappedColumns: Map<string, DataFrameColumnDef>
): Record<string, string> {
  return objectFromEntries([...mappedColumns.keys()].map((key) => [key, mappedColumns.get(key)!.systemName]));
}

/**
 * Find the best matching column in `selectedColumns` for a given `templateColumn`, by relative edit distance.
 *
 * @returns The matched column and its relative edit distance, or null if none found.
 */
function findBestMatchByRelativeEditDistance(
  templateColumn: DataFrameColumnDef,
  selectedColumns: Array<DataFrameColumnDef>
): {column: DataFrameColumnDef; distance: number} | null {
  if (selectedColumns.length === 0) {
    return null;
  }

  const matchedColumn = selectedColumns.reduce(
    (minColumn, selectedColumn) => {
      const currentDistance = getRelativeEditDistanceBetweenColumns(templateColumn, selectedColumn);
      return currentDistance < minColumn.distance
        ? {column: selectedColumn, distance: currentDistance}
        : minColumn;
    },
    {column: selectedColumns[0], distance: 1}
  );

  return matchedColumn;
}

/**
 * Generate a default column mapping from the template columns to the selected columns.
 *
 * If there are exact column matches by system name, with compatible types, these are mapped first.
 *
 * Then, the default column mapping will map selected columns to template columns by minimum relative edit
 * distance. If minimum relative edit distance is greater than MAX_EDIT_DISTANCE_ALLOWED, it will map from
 * the first selected column with a compatible column type.
 *
 * @param templateColumns The template columns to map to.
 * @param selectedColumns The selected columns to map from.
 * @returns A map of template column system names to selected columns' system names.
 */
export function getDefaultDataFrameColumnMapping(
  templateColumns: Array<DataFrameColumnDef>,
  selectedColumns: Array<DataFrameColumnDef>
): Record<string, string> {
  const columnMapping = new Map<string, DataFrameColumnDef>();
  const usedSelectedColumns = new Map<string, DataFrameColumnDef>();

  // If there are exact matches by system name (with compatible types), map those first.
  // This makes column mapping compatible with data frames whose display names change, as long as the
  // system names remain the same. (e.g. This is used to make system data frame display name changes
  // backwards compatible with old templates created on them.)
  templateColumns.forEach((templateColumn) => {
    const matchedColumn = selectedColumns.find((column) => column.systemName === templateColumn.systemName);
    if (matchedColumn && isColumnTypeCompatible(matchedColumn, templateColumn)) {
      columnMapping.set(templateColumn.systemName, matchedColumn);
      usedSelectedColumns.set(matchedColumn.systemName, matchedColumn);
    }
  });

  // Match selected columns to template columns by minimum relative edit distance.
  templateColumns.forEach((templateColumn) => {
    if (!columnMapping.has(templateColumn.systemName)) {
      const matchedColumn = findBestMatchByRelativeEditDistance(
        templateColumn,
        // Only consider available columns (not already matched in previous step)
        selectedColumns.filter((column) => !usedSelectedColumns.has(column.systemName))
      );
      if (matchedColumn && matchedColumn.distance <= MAX_EDIT_DISTANCE_ALLOWED) {
        columnMapping.set(templateColumn.systemName, matchedColumn.column);
        usedSelectedColumns.set(matchedColumn.column.systemName, matchedColumn.column);
      }
    }
  });

  templateColumns.forEach((templateColumn) => {
    if (!columnMapping.has(templateColumn.systemName)) {
      const matchedColumn = selectedColumns.find(
        (column) =>
          !usedSelectedColumns.has(column.systemName) &&
          // We're just trying to make a best-effort match based on types, and don't need to be strict
          // around if both types are nullable
          isColumnTypeCompatible(column, templateColumn, {alwaysAllowNullable: true})
      );
      if (matchedColumn) {
        columnMapping.set(templateColumn.systemName, matchedColumn);
        usedSelectedColumns.set(matchedColumn.systemName, matchedColumn);
      }
    }
  });
  return convertColumnDefMappingToSystemNameMapping(columnMapping);
}

/**
 * Check if a column mapping is complete, i.e. every column in the template data frame is mapped to a non-null
 * column def.
 *
 * @param templateColumns The template data frame columns to map to
 * @param mappedColumns A map of template column system names to selected columns' system names.
 * @returns True is the mapping is complete
 */
export function isDataFrameColumnMappingComplete(
  templateColumns: Array<DataFrameColumnDef>,
  mappedColumns: Record<string, string>
): boolean {
  return templateColumns.every(({systemName}) => mappedColumns[systemName] != null);
}

/**
 * Filter a column mapping to exclude invalid column system names.
 *
 * Duplicate values from `targetColumns` are not removed.
 *
 * @param sourceColumns Column definitions, whose system names are the keys of `columnMapping`.
 * @param targetColumns Column definitions, whose system names are the values of `columnMapping`.
 * @param columnMapping A mapping of column system names to column system names.
 * @returns A filtered versionof `columnMapping`, with any entries referencing column system names not in
 *          `sourceColumns` or `targetColumns` removed.
 */
export function filterInvalidColumnsFromColumnMapping(
  sourceColumns: Array<DataFrameColumnDef>,
  targetColumns: Array<DataFrameColumnDef>,
  columnMapping: Record<string, string>
): Record<string, string> {
  const sourceColumnSystemNames = new Set(sourceColumns.map(({systemName}) => systemName));
  const targetColumnSystemNames = new Set(targetColumns.map(({systemName}) => systemName));

  return objectFromEntries(
    Object.keys(columnMapping)
      .filter((key) => sourceColumnSystemNames.has(key) && targetColumnSystemNames.has(columnMapping[key]))
      .map((key) => [key, columnMapping[key]])
  );
}

/*
 * Given two set of columns, use one as a template
 *  and align the other set of columns to the template.
 * By name: (using display name, and secondarily type)
 *  if a column exists in both sets, align them by name.
 *  if the template set contains additional columns, align them to null.
 *  if the the aligning set contains additional columns, ignore them.
 * By order:
 *  if the template set is longer than the aligning set, align the rest to null.
 *  if the aligning set is longer than the template set, ignore the rest.
 */
export function autoMapDataFrameColumns(
  templateColumns: Array<DataFrameColumnDef>,
  aligningColumns: Array<DataFrameColumnDef>,
  autoMapOption: AutoMapperOption
): {
  autoMappedTemplateColumns: Array<DataFrameColumnDef | null>;
  autoMappedAligningColumns: Array<DataFrameColumnDef | null>;
} {
  const autoMappedTemplateColumns: Array<DataFrameColumnDef | null> = [];
  const autoMappedAligningColumns: Array<DataFrameColumnDef | null> = [];
  switch (autoMapOption.mode) {
    case AutoMapperMode.NAME: {
      const displayNameIndexedAligningColumns: Record<string, Array<DataFrameColumnDef>> = {};
      for (const col of aligningColumns) {
        displayNameIndexedAligningColumns[col.displayName] =
          displayNameIndexedAligningColumns[col.displayName] || [];
        displayNameIndexedAligningColumns[col.displayName].push(col);
      }

      // Do an outer join first and then filter out the columns that are not compatible with each join type
      const columnPairs: Array<[DataFrameColumnDef | null, DataFrameColumnDef | null]> = [];
      for (const colTemplate of templateColumns) {
        const colAlignCandidates = displayNameIndexedAligningColumns[colTemplate.displayName] ?? [];
        const colAlignNameTypeMatch =
          colAlignCandidates.find((colAlign) => isColumnTypeCompatible(colAlign, colTemplate)) || null;
        const colAlignNameMatch = colAlignCandidates[0] ?? null;
        columnPairs.push([colTemplate, colAlignNameTypeMatch ?? colAlignNameMatch]);
      }
      const autoMappedAligningColumnsMap = new Set<DataFrameColumnDef | null>(
        columnPairs.map(([_leftCol, rightCol]) => rightCol)
      );
      for (const colAlign of aligningColumns) {
        if (!autoMappedAligningColumnsMap.has(colAlign)) {
          columnPairs.push([null, colAlign]);
        }
      }

      if (autoMapOption.columnJoinType === AutoMapperColumnJoinType.KEEP_TEMPLATE) {
        const leftJoinColumnPairs = columnPairs.filter(([leftCol, _rightCol]) => leftCol != null);
        autoMappedTemplateColumns.push(...leftJoinColumnPairs.map(([leftCol, _rightCol]) => leftCol));
        autoMappedAligningColumns.push(...leftJoinColumnPairs.map(([_leftCol, rightCol]) => rightCol));
      } else if (autoMapOption.columnJoinType === AutoMapperColumnJoinType.KEEP_SHARED) {
        const innerJoinColumnPairs = columnPairs.filter(
          ([leftCol, rightCol]) => leftCol != null && rightCol != null
        );
        autoMappedTemplateColumns.push(...innerJoinColumnPairs.map(([leftCol, _rightCol]) => leftCol));
        autoMappedAligningColumns.push(...innerJoinColumnPairs.map(([_leftCol, rightCol]) => rightCol));
      } else if (autoMapOption.columnJoinType === AutoMapperColumnJoinType.KEEP_ALL) {
        autoMappedTemplateColumns.push(...columnPairs.map(([leftCol, _rightCol]) => leftCol));
        autoMappedAligningColumns.push(...columnPairs.map(([_leftCol, rightCol]) => rightCol));
      }
      break;
    }
    case AutoMapperMode.ORDER: {
      if (autoMapOption.columnJoinType === AutoMapperColumnJoinType.KEEP_TEMPLATE) {
        for (let i = 0; i < templateColumns.length; i++) {
          autoMappedTemplateColumns.push(templateColumns[i]);
          autoMappedAligningColumns.push(aligningColumns[i] ?? null);
        }
      } else if (autoMapOption.columnJoinType === AutoMapperColumnJoinType.KEEP_SHARED) {
        for (let i = 0; i < Math.min(templateColumns.length, aligningColumns.length); i++) {
          autoMappedTemplateColumns.push(templateColumns[i]);
          autoMappedAligningColumns.push(aligningColumns[i]);
        }
      } else if (autoMapOption.columnJoinType === AutoMapperColumnJoinType.KEEP_ALL) {
        for (let i = 0; i < Math.max(templateColumns.length, aligningColumns.length); i++) {
          autoMappedTemplateColumns.push(templateColumns[i] ?? null);
          autoMappedAligningColumns.push(aligningColumns[i] ?? null);
        }
      } else {
        // oxlint-disable-next-line no-unused-expressions
        autoMapOption.columnJoinType satisfies never;
      }
      break;
    }
    default:
      // oxlint-disable-next-line no-unused-expressions
      autoMapOption.mode satisfies never;
  }

  return {autoMappedTemplateColumns, autoMappedAligningColumns};
}

/**
 * Convert from the "selected columns" array used by <DataFrameColumnMappingTable> to a mapping of column
 * system names.
 *
 * @param selectedColumns An array of column defs, in which each column def corresponds to the column def at
 *                        the same index in `templateDataFrameColumns`.
 * @param templateDataFrameColumns The column defs of the template's data frame.
 * @returns A mapping from column system names (of the template's data frame) to column system names (of the
 *          input data frame).
 */
export function selectedColumnsToColumnMappings(
  selectedColumns: Array<DataFrameColumnDef | null>,
  templateDataFrameColumns: Array<DataFrameColumnDef>
): Record<string, string> {
  return selectedColumns.reduce(
    (acc: Record<string, string>, columnOrNull: DataFrameColumnDef | null, index: number) => {
      if (columnOrNull) {
        acc[templateDataFrameColumns[index].systemName] = columnOrNull.systemName;
      }
      return acc;
    },
    {}
  );
}

/**
 * Convert from a mapping of column system names to the "selected columns" array used by
 * <DataFrameColumnMappingTable>.
 *
 * @param columnMapping A mapping from column system names (of the template's data frame) to column system names (of the
 *                      input data frame).
 * @param templateDataFrameColumns The column defs of the template's data frame.
 * @param inputDataFrameColumns The column defs of the input data frame.
 * @return An array of column defs, in which each column def corresponds to the column def at the same index
 *         in `templateDataFrameColumns`.
 */
export function columnMappingToSelectedColumns(
  columnMapping: Record<string, string>,
  templateDataFrameColumns: Array<DataFrameColumnDef>,
  inputDataFrameColumns: Array<DataFrameColumnDef>
): Array<DataFrameColumnDef | null> {
  const inputDataFrameColumnsBySystemName = objectFromEntries(
    inputDataFrameColumns.map((columnDef) => [columnDef.systemName, columnDef])
  );
  return templateDataFrameColumns
    .map(({systemName}) => columnMapping[systemName])
    .map((inputColumnSystemName) => inputDataFrameColumnsBySystemName[inputColumnSystemName] ?? null);
}

export function unselectColumnsIfColumnIsNotInTemplateColumns(
  selectedColumns: Array<DataFrameColumnDef | null>,
  selectableColumns: Array<DataFrameColumnDef>,
  setSelectedColumns: (selectedColumns: Array<DataFrameColumnDef | null>) => void
): void {
  // This function is used to unselect columns if the column is not in the template columns
  // If a user regenerates a preview, then the new preview might not have a previously
  // selected column so we must remove it from state.
  const selectableColumnSystemNames = selectableColumns.map(
    (selectableColumn) => selectableColumn.systemName
  );
  const systemNamesToUnselect = selectedColumns
    .filter((column) => column != null && !selectableColumnSystemNames.includes(column.systemName))
    .map((column: DataFrameColumnDef) => column.systemName);

  if (systemNamesToUnselect.length > 0) {
    const newSelectedColumns = selectedColumns.map((selectedColumn) => {
      if (selectedColumn == null || systemNamesToUnselect.includes(selectedColumn.systemName)) {
        return null;
      }
      return selectedColumn;
    });
    setSelectedColumns(newSelectedColumns);
  }
}

export function getTemplateDataFrameColumns(
  analysis: AnalysisGraphQL,
  stepIdToReplace: string | undefined
): Array<DataFrameColumnDef> {
  if (stepIdToReplace == null) {
    return [];
  }
  const step = getLegacyNullableStepFromAnalysis(analysis, stepIdToReplace)!;
  if (step == null) {
    // After a clicks submit, the frontend could try to render after the step is deleted
    // This return value doesn't matter because the page is reloading, but it prevents the user from seeing an error
    return [];
  }
  const childSteps = getChildSteps(analysis, step);
  if (
    childSteps.length === 1 &&
    childSteps[0].definition.stepType === PipelineStepType.MAP_COLUMNS &&
    childSteps[0].statusCode === PipelineStepStatusCode.SUCCEEDED
  ) {
    // If there is already a column mapping step after the step to replace, we will reuse it and map to its outputs.
    // Adding multiple column mapping steps is hard to debug.
    const columnMappingStep = childSteps[0];
    return findAndAssertSingleStepDataFrame(columnMappingStep, columnMappingStep.outputStepDatas[0].name)
      .allColumnsJson;
  } else if (childSteps.length === 1 && childSteps[0].definition.stepType === PipelineStepType.MAP_COLUMNS) {
    // You cannot replace a step if it is failed (you have to delete it), but if there is a case where the initialize table step
    // succeeds and the column mapping step is failed, we should debug it.
    assertAndContinue(
      false,
      `Attempted to replace a step with a failed column mapping child ${childSteps[0].statusCode}`
    );
  }
  // We can assert that the step has a single data frame because replace doesn't work on multiple output steps
  const dataFrame = findAndAssertSingleStepDataFrame(step, step.outputStepDatas[0].name);
  return dataFrame.allColumnsJson;
}

/**
 * When creating a table with upload CSV or from a connection, the frontend does not have system names before the finalize function is run because the dataframe
 * is created by the OFM while the step is executed. To let users column map for these, we assign each column a 'system name' that is just the stringified index.
 * The backend will map these indices to the actual system names, and our frontend components can use these system names for the column mapping, column dropdown
 * selector components, etc.
 *
 * @param headers array of string display names, the first row in a parsed csv
 * @returns array of DataFrameColumnDef objects with systemName set to the index of the header
 */
export function csvHeadersToPlaceholderColumnDefs(headers: Array<string>): Array<DataFrameColumnDef> {
  return headers.map((displayName: string, index: number) => ({
    systemName: index.toString(),
    displayName,
    // Arbitrary type, real type is determined by backend
    type: {leafType: LeafType.TEXT},
  }));
}

export function setPreviewDataFrameColumnsFromFileString(
  fileText: string | null,
  setPreviewDataFrame: (dataFrame: {columns: Array<DataFrameColumnDef>} | null) => void
): void {
  let columns: Array<DataFrameColumnDef> = [];
  if (fileText == null) {
    setPreviewDataFrame(null);
  } else {
    const rows = parseRows(fileText);
    columns = csvHeadersToPlaceholderColumnDefs(rows[0]);
    setPreviewDataFrame({columns});
  }
}
